



























import {
  Vue,
  Component,
  Prop,
  Ref,
  Watch,
  Mixins,
} from "vue-property-decorator";
import companyMap from "@/components/Company/Map.vue";
import { currentCompanyStore } from "@/store/typed";
import StoreSubscriber from "@/utils/StoreSubscriber";
import { GOOGLE_MAPS_API_KEY } from "../../utils/Constants";

import ScoutingDetails from "@/components/Scouting/Details.vue";
import CompanyPlaceDetails from "@/components/CompanyPlace/Details.vue";
import { NavigationGuardNext, Route } from "vue-router";
import CompanyPlace from "@/models/CompanyPlace";
import Company from "@/models/Company";
import VSignPost from "@/components/Company/VSignPost.vue";
import CompanyMixin from "@/mixins/http/CompanyMixin";
import VMiniLogo from "@/components/VMiniLogo.vue";

declare global {
  interface Window {
    google?: any;
  }
}

Component.registerHooks(["beforeRouteUpdate"]);

@Component({
  components: { companyMap, ScoutingDetails, CompanyPlaceDetails, VMiniLogo },
})
export default class Map extends Mixins(CompanyMixin) {
  @Ref("companyMap") readonly companyMap!: companyMap;
  @Ref() readonly detailsView?: ScoutingDetails;

  private googleMapsLoaded = false;
  private detailsType = "";
  company: Company | null = null;

  async beforeRouteUpdate(to: Route, _from: Route, next: NavigationGuardNext) {
    await currentCompanyStore.loadCompanyPlaces(this.$route.params.id);
    next();
  }

  async mounted() {
    this.company = await this.getCompany(this.$route.params.id);
    await currentCompanyStore.loadCompanyPlaces(this.$route.params.id);
    await this.loadGoogleMaps();
    if (this.companyPlaces?.length !== 0) {
      this.updateMarkers(this.companyPlaces);
      this.companyMap?.centerOnPlaces(this.companyPlaces);
    }
  }

  get companyPlaces() {
    return currentCompanyStore.companyPlaces;
  }

  subscribe() {
    StoreSubscriber.subscribe(
      "currentCompany/updateCompanyPlace",
      this.onCompanyPlaceUpdated.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$updateSearchCompanyPlace",
      this.onCompanyPlaceSearchUpdated.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$centerOnMarker",
      this.onCenterOnMarker.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/removeCompanyPlace",
      this.onCompanyPlaceRemoved.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$centerOnMarkers",
      this.onCenterOnMarkers.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$addSearchCompanyPlaces",
      this.onSearchCompanyPlacesAdded.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$clearMarkers",
      this.onClearMarkers.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$setSearchCompanyPlaces",
      this.onSearchCompanyPlacesSet.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$saveScouting",
      this.onSaveScouting.bind(this)
    );
    StoreSubscriber.subscribe(
      "currentCompany/$addOrReplace",
      this.onAddOrReplace.bind(this)
    );
  }

  onSaveScouting() {
    this.detailsView?.open();
  }

  onCompanyPlaceUpdated(payload: CompanyPlace) {
    payload.highlighted = true;
    this.companyMap?.updateMarker(payload);
    this.companyMap?.centerOnPlaces(this.companyPlaces);
  }

  onCompanyPlaceSearchUpdated(payload: CompanyPlace) {
    this.companyMap?.updateMarker(payload);
  }

  onClearMarkers() {
    this.companyMap?.clearMarkers();
  }

  onSearchCompanyPlacesAdded(payload: CompanyPlace[]) {
    this.updateMarkers(payload, false);
  }

  onSearchCompanyPlacesSet(payload: CompanyPlace[]) {
    this.updateMarkers(payload);
  }

  onCenterOnMarker(payload: CompanyPlace) {
    this.companyMap.centerOnPlace(payload);
  }

  onCompanyPlaceRemoved(id: string) {
    this.companyMap?.removeMarker(id);
  }

  onAddOrReplace(payload: CompanyPlace) {
    if (payload.position) {
      this.companyMap?.centerOnMarkers();
    }
  }

  @Watch("companyPlaces")
  onCompanyPlaceChanged(values: CompanyPlace[]) {
    if (this.companyPlaces && this.companyMap) {
      this.updateMarkers(this.companyPlaces);
      this.companyMap.centerOnPlaces(this.companyPlaces);
    }
  }

  get scriptUrl() {
    return `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=it`;
  }

  async loadGoogleMaps() {
    if (!window.google?.maps?.places) {
      const plugin = document.createElement("script");
      plugin.setAttribute("src", this.scriptUrl);
      plugin.async = true;
      document.head.appendChild(plugin);
      await new Promise((resolve) => (plugin.onload = resolve));
    }
    this.googleMapsLoaded = true;
    this.initView();
  }

  onCenterOnMarkers() {
    this.companyMap?.centerOnMarkers();
  }

  initView() {
    this.subscribe();
  }

  private updateMarkers(points: CompanyPlace[], clear = true) {
    if (clear) {
      this.companyMap?.clearMarkers();
    }
    for (let place of points) {
      this.companyMap?.addMarker(place);
    }
    this.companyMap?.centerOnMarkers();
  }

  logoPath(value: Company) {
    return VSignPost.removeHeadingBackSlash(value.croppedLogo || "");
  }
}
